import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import useAskAssistant from "ai/hooks/useAskAssistant";
import useDisclosure from "util/hooks/useDisclosure";
import { useInvalidateAIQuotaStatus } from "ai/hooks/useAIQuotaStatus";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { getPathnameWithoutId } from "util/URLUtils";
import useLocation from "routing/useLocation";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
export const AssistantSidebarContext = React.createContext(void 0);
export const AssistantSidebarProvider = ({ children }) => {
  const [isOpen, { open, close, toggle }] = useDisclosure();
  const [pendingRequest, setPendingRequest] = useState(null);
  const { request } = useAskAssistant();
  const refetchQuota = useInvalidateAIQuotaStatus();
  const sendTelemetry = useSendTelemetry();
  const { pathname } = useLocation();
  const startPendingRequest = useCallback(
    (r) => setPendingRequest({ isPending: true, request: r }),
    []
  );
  const clearPendingRequest = useCallback(() => setPendingRequest(null), []);
  const setPendingError = useCallback(
    (error, retry) => setPendingRequest((current) => ({ isPending: false, error, retry, request: current.request })),
    []
  );
  const generate = useCallback(
    (r) => {
      const mutation = () => request(r).then(clearPendingRequest).then(refetchQuota);
      startPendingRequest(r);
      mutation().then(
        () => {
          sendTelemetry(TELEMETRY_EVENT_TYPE.WIDGET_SUMMARY.REQUEST_SUCCESS, {
            app_pathname: getPathnameWithoutId(pathname)
          });
        },
        (error) => {
          sendTelemetry(TELEMETRY_EVENT_TYPE.WIDGET_SUMMARY.REQUEST_FAILURE, {
            app_pathname: getPathnameWithoutId(pathname)
          });
          setPendingError(error.message, () => generate(r));
        }
      );
      open();
    },
    [clearPendingRequest, open, pathname, refetchQuota, request, sendTelemetry, setPendingError, startPendingRequest]
  );
  const value = useMemo(
    () => ({ isOpen, toggle, open, close, generate, pendingRequest }),
    [isOpen, toggle, open, close, generate, pendingRequest]
  );
  return /* @__PURE__ */ React.createElement(AssistantSidebarContext.Provider, { value }, children);
};
